import { useEffect, useRef, useState } from 'react';

import { BodyRegular } from '../typography';

type SegmentedControlProps = {
  selectedTab: number;
  setSelectedTab: (index: number) => void;
  tabLabels: string[];
  dataCyValues: string[];
};

type PillStyle = {
  left: number;
  width: number;
  isAnimated: boolean;
};

const SegmentedControl = ({
  selectedTab,
  tabLabels,
  setSelectedTab,
  dataCyValues
}: SegmentedControlProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pillStyle, setPillStyle] = useState<PillStyle | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measurePill = (isAnimated: boolean) => {
      const tabElements = container.querySelectorAll<HTMLElement>(
        '[data-segmented-tab]'
      );
      const activeTab = tabElements[selectedTab];
      if (!activeTab) return;

      setPillStyle({
        left: activeTab.offsetLeft,
        width: activeTab.offsetWidth,
        isAnimated
      });
    };

    measurePill(true);

    // The container can reflow after mount (siblings appearing once their
    // data loads, font swaps, rotation), which leaves the measured pill
    // stale. Re-measure on any container resize, snapping rather than
    // animating so corrections are not visible as a slide. The observer
    // always fires once on observe with the initial size, which would
    // otherwise cancel the tab-change animation, so skip that call.
    let hasObservedInitialSize = false;
    const resizeObserver = new ResizeObserver(() => {
      if (!hasObservedInitialSize) {
        hasObservedInitialSize = true;
        return;
      }
      measurePill(false);
    });
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [selectedTab, tabLabels.length]);

  return (
    <div
      ref={containerRef}
      className="pointer relative flex w-full rounded-lg bg-primary-100 p-1 md:w-min"
    >
      {pillStyle && (
        <div
          className={`absolute top-1 bottom-1 rounded-lg bg-neutral-50 shadow-sm ${
            pillStyle.isAnimated
              ? 'transition-all duration-300 ease-in-out'
              : ''
          }`}
          style={{
            left: pillStyle.left,
            width: pillStyle.width
          }}
        />
      )}
      {tabLabels.map((text, index) => (
        <BodyRegular
          key={`tab-${text}-${index}`}
          onClick={() => setSelectedTab(index)}
          data-segmented-tab
          className={`relative z-10 flex-1 cursor-pointer whitespace-nowrap px-1 py-2 text-center text-xs transition-colors duration-300 sm:text-base md:px-2 ${
            selectedTab === index
              ? 'font-semibold text-neutral-800'
              : 'text-neutral-500'
          }`}
          data-cy={dataCyValues[index]}
        >
          {text}
        </BodyRegular>
      ))}
    </div>
  );
};

export default SegmentedControl;
